/**
 * Advertisement video section rendered directly below the hero carousel.
 *
 * Behaviours (iter25 spec):
 *   • Muted autoplay (browser-policy compliant), loops forever.
 *   • On `ended` → 3-second pure-black overlay → playback restarts at t=0.
 *   • **Controls are hidden by default**. They appear on any pointer/touch
 *     interaction with the video and auto-hide after ~3 s of inactivity.
 *   • Control bar order, left → right:
 *        Play/Pause · Progress bar · Time · Enlarge / Shrink
 *   • Mute/Unmute speaker pill stays in the top-right (separate corner so
 *     it's always reachable on mobile).
 *   • Enlarge button switches the video into an immersive overlay
 *     (fixed inset-0, z-50, body scroll locked). The overlay uses
 *     `object-contain` so the video keeps its natural aspect ratio in BOTH
 *     portrait and landscape device orientations — the user can rotate
 *     freely. Shrink returns to the homepage at the exact scroll position
 *     (preserved because we never navigate — we just toggle state).
 *
 * Sources:
 *   The video plays the WebM source on browsers that support VP9 (smaller
 *   file, also what open-source Chromium / Playwright test runners can
 *   decode); browsers without VP9 fall through to the H.264 MP4 source.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Minimize2 } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

// Source URLs are constants (no runtime config). If you swap the creative
// in the future, update both lines and bump the cache buster as needed.
const AD_VIDEO_MP4 =
  "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/rslwgeow_download_20260617_221950_0000.mp4";
const AD_VIDEO_WEBM = "/ad-video.webm";
// Iter28 second advertisement (Sri Durga Foods Spices ad)
const AD_VIDEO_2_MP4 =
  "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/w6f6pusl_Sri%20Durga%20Foods%20Spices%28Advertisement%29.mp4";

const DEFAULT_VIDEOS = [
  { id: "default-1", src_mp4: AD_VIDEO_MP4, src_webm: AD_VIDEO_WEBM, poster: "" },
  { id: "default-2", src_mp4: AD_VIDEO_2_MP4, src_webm: "", poster: "" },
];

const CONTROLS_HIDE_DELAY_MS = 5000;
const VIDEO_GAP_MS = 2000;

export default function AdVideoSection({ available = true }) {
  const { settings } = useSettings();
  // null/undefined → never configured → use the 2 bundled defaults.
  // Empty array [] → admin explicitly cleared all videos → hide section.
  const savedVideos = settings?.hero_videos;
  const videos = Array.isArray(savedVideos)
    ? (savedVideos.length ? savedVideos : null)
    : DEFAULT_VIDEOS;
  const [videoIdx, setVideoIdx] = useState(0);

  const videoRef = useRef(null);
  const loopTimerRef = useRef(null);
  const controlsHideTimerRef = useRef(null);
  // Audio state must survive video switches — keep it in a ref so the
  // effect below can restore it after the new <video> mounts.
  const mutedRef = useRef(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMutedState] = useState(true);
  const setMuted = useCallback((next) => {
    mutedRef.current = next;
    setMutedState(next);
  }, []);
  const [progress, setProgress] = useState(0);   // 0..1
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blackScreen, setBlackScreen] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const containerRef = useRef(null);
  const [controlsVisible, setControlsVisible] = useState(false);

  // Conditional render lives after every hook to keep hook order stable.
  const current = videos ? (videos[Math.min(videoIdx, videos.length - 1)] || videos[0]) : null;

  // ── Controls visibility timer ────────────────────────────────────────────
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (controlsHideTimerRef.current) {
      clearTimeout(controlsHideTimerRef.current);
    }
    controlsHideTimerRef.current = setTimeout(() => {
      setControlsVisible(false);
      controlsHideTimerRef.current = null;
    }, CONTROLS_HIDE_DELAY_MS);
  }, []);

  // ── Video element wiring ─────────────────────────────────────────────────
  useEffect(() => {
    if (!available) return;
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrentTime(v.currentTime);
      if (v.duration > 0) setProgress(v.currentTime / v.duration);
    };
    const onMeta = () => setDuration(v.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setBlackScreen(true);
      setPlaying(false);
      loopTimerRef.current = setTimeout(() => {
        setBlackScreen(false);
        // Advance to next video in carousel (or loop back to first) after
        // a 3-second gap.  Audio state (mutedRef.current) is preserved.
        if (videos.length > 1) {
          setVideoIdx((idx) => (idx + 1) % videos.length);
        } else if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {});
        }
        loopTimerRef.current = null;
      }, VIDEO_GAP_MS);
    };
    const onCanPlay = () => { if (v.paused) v.play().catch(() => {}); };

    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    v.addEventListener("canplay", onCanPlay);

    // Restore the user's audio preference across video switches.
    v.muted = mutedRef.current;
    try { v.load(); } catch (_) { /* legacy browsers may not implement load() */ }
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();

    // Some mobile browsers only allow autoplay after first user interaction.
    const kick = () => {
      tryPlay();
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("touchstart", kick);
    };
    window.addEventListener("pointerdown", kick, { once: true });
    window.addEventListener("touchstart", kick, { once: true });

    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("canplay", onCanPlay);
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("touchstart", kick);
      if (loopTimerRef.current) {
        clearTimeout(loopTimerRef.current);
        loopTimerRef.current = null;
      }
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
    };
  }, [available, current?.src_mp4, videos?.length]);

  // ── Body scroll lock + ESC key + real Fullscreen API while enlarged ─────
  // We layer THREE protections so the enlarged video has no bottom gap on
  // any device:
  //   1. Body overflow:hidden  (kills scroll under the overlay)
  //   2. Real Fullscreen API on the container element when supported. This
  //      makes Android Chrome / Samsung Internet hide the URL bar + status
  //      bar completely — no chrome can re-appear on rotation or unmute.
  //   3. CSS-only fallback (100dvh / 100svh) covers iOS Safari, which
  //      doesn't support requestFullscreen on arbitrary elements; dvh tracks
  //      the dynamic viewport height so the bottom bar appearing/disappearing
  //      doesn't leave a gap.
  // We also try to lock orientation to landscape on touch devices (silently
  // fails on iOS Safari which doesn't support Screen Orientation API yet).
  useEffect(() => {
    if (!enlarged) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const node = containerRef.current;
    const reqFs = node && (node.requestFullscreen || node.webkitRequestFullscreen || node.webkitEnterFullscreen || node.mozRequestFullScreen || node.msRequestFullscreen);
    if (reqFs && !document.fullscreenElement) {
      try { reqFs.call(node).catch(() => {}); } catch (_) { /* ignore */ }
    }
    // Best-effort orientation lock for landscape video on Android.
    try {
      if (window.screen?.orientation?.lock) {
        window.screen.orientation.lock("landscape").catch(() => {});
      }
    } catch (_) { /* ignore — iOS Safari etc. */ }

    const onKey = (e) => { if (e.key === "Escape") setEnlarged(false); };
    const onFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl && enlarged) setEnlarged(false); // user pressed Esc / back gesture
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      // Exit fullscreen + release orientation lock when leaving enlarged.
      try { if (window.screen?.orientation?.unlock) window.screen.orientation.unlock(); } catch (_) { /* ignore */ }
      const exitFs = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      if (exitFs && (document.fullscreenElement || document.webkitFullscreenElement)) {
        try { exitFs.call(document); } catch (_) { /* ignore */ }
      }
    };
  }, [enlarged]);

  // ── Control handlers ────────────────────────────────────────────────────
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
    showControls();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.paused) v.play().catch(() => {});
    showControls();
  };

  // Seek bar: support BOTH a single click/tap AND a smooth drag (mouse +
  // touch). While a drag is in progress we keep the video at the user's
  // target position even between requestAnimationFrame ticks.
  const seekingRef = useRef(false);

  const seekFromClientX = useCallback((clientX, bar) => {
    const v = videoRef.current;
    if (!v || !v.duration || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
    setProgress(pct);
    setCurrentTime(pct * v.duration);
  }, []);

  const onSeekPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const bar = e.currentTarget;
    seekingRef.current = true;
    showControls();
    seekFromClientX(e.clientX, bar);
    // Capture the pointer so subsequent moves/up come to this element even
    // if the finger drifts outside it.
    if (e.pointerId !== undefined) {
      try { bar.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  };

  const onSeekPointerMove = (e) => {
    if (!seekingRef.current) return;
    e.preventDefault();
    seekFromClientX(e.clientX, e.currentTarget);
  };

  const onSeekPointerUp = (e) => {
    if (!seekingRef.current) return;
    seekingRef.current = false;
    if (e.pointerId !== undefined) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
    showControls();
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "00:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  if (!available) return null;

  // ── Shared video + controls block ───────────────────────────────────────
  // Wrapping the same video element in BOTH the inline section AND the
  // enlarged overlay would create two DOM nodes, which means losing
  // currentTime / muted state across the transition. Instead we render one
  // VideoBox and CSS-style it differently based on `enlarged`.

  // YouTube-style behaviour: tapping the video toggles control visibility.
  // While controls are visible, a second tap immediately hides them.
  // While hidden, a tap surfaces them (and they auto-hide after the delay).
  const onVideoActivate = () => {
    if (controlsVisible) {
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
      setControlsVisible(false);
    } else {
      showControls();
    }
  };

  // Class + inline-style set for the OUTER frame.
  //   Enlarged mode uses BOTH `fixed inset-0` (legacy fallback) AND inline
  //   `100svh` height — when the browser supports `svh` it tracks the small
  //   viewport (URL bar collapsed) so there's no bottom gap when the user
  //   un-mutes or rotates.  When real Fullscreen API engages (Android
  //   Chrome / Samsung Internet) this becomes a no-op because the element
  //   actually IS the viewport.
  const frameClass = enlarged
    ? "fixed inset-0 z-[120] bg-black flex items-center justify-center"
    : "relative w-full max-w-7xl mx-auto rounded-2xl overflow-hidden bg-black shadow-[0_30px_80px_-40px_rgba(46,16,16,0.6)]";
  const frameStyle = enlarged
    ? { height: "100svh", width: "100vw", maxHeight: "100dvh" }
    : undefined;

  const aspectClass = enlarged
    ? "relative w-full h-full bg-black"
    : "relative aspect-video bg-black";

  // CONTROLS — only render when visible; when enlarged we float them at the
  // bottom of the viewport with a soft gradient so they're legible against
  // any video frame.
  const controlsClass = enlarged
    ? `absolute left-0 right-0 bottom-0 z-20 flex items-center gap-3 sm:gap-4 px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-brand-cream transition-opacity duration-300 ${controlsVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`
    : `absolute left-0 right-0 bottom-0 z-20 flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-t from-black/90 via-black/55 to-transparent text-brand-cream transition-opacity duration-300 ${controlsVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`;

  // Bail out BEFORE building the JSX — `sectionContent` accesses
  // `current.id` / `current.poster` etc and would crash if `current` is
  // null (admin cleared all videos).
  if (!videos || !current) return null;

  const sectionContent = (
    <div ref={containerRef} className={frameClass} style={frameStyle} data-testid={enlarged ? "ad-video-enlarged" : "ad-video-card"}>
      <div className={aspectClass}>
        {/* Tap surface — anywhere on the video reveals controls */}
        <video
          ref={videoRef}
          key={current.id}
          className={`absolute inset-0 w-full h-full object-contain bg-black transition-opacity duration-300 ${blackScreen ? "opacity-0" : "opacity-100"}`}
          playsInline
          muted
          autoPlay
          preload="auto"
          poster={current.poster || undefined}
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          data-testid="ad-video"
        >
          {current.src_webm && <source src={current.src_webm} type="video/webm" />}
          <source src={current.src_mp4 || AD_VIDEO_MP4} type="video/mp4" />
        </video>

        {/* Transparent tap-capture overlay above the <video> element — this
            avoids the double-fire that happens when the browser dispatches
            both `touchstart` AND a synthesized `click` onto the video, and
            also dodges the OS-default video native control gesture. */}
        <button
          type="button"
          aria-label="Toggle video controls"
          onClick={onVideoActivate}
          className="absolute inset-0 w-full h-full z-10 bg-transparent cursor-pointer"
          style={{ touchAction: "manipulation" }}
          data-testid="ad-video-tap-capture"
        />
        {blackScreen && (
          <div className="absolute inset-0 bg-black" aria-hidden="true" data-testid="ad-video-blackscreen" />
        )}

        {/* MUTE — always reachable; pulses into view with the rest of the controls */}
        <button
          type="button"
          onClick={toggleMute}
          className={`absolute top-3 right-3 sm:top-4 sm:right-4 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm text-white border border-white/20 flex items-center justify-center transition-all duration-300 active:scale-95 z-20 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-label={muted ? "Unmute video" : "Mute video"}
          data-testid="ad-video-mute"
        >
          {muted ? (
            <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
          ) : (
            <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
          )}
        </button>

        {/* CUSTOM CONTROLS BAR — Play · Progress · Time · Enlarge */}
        <div className={controlsClass}>
          <button
            type="button"
            onClick={togglePlay}
            className={`${enlarged ? "w-11 h-11 sm:w-12 sm:h-12" : "w-9 h-9 sm:w-10 sm:h-10"} rounded-full bg-brand-turmeric text-brand-dark flex items-center justify-center shrink-0 transition-transform active:scale-95`}
            aria-label={playing ? "Pause video" : "Play video"}
            data-testid="ad-video-playpause"
          >
            {playing ? <Pause className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.4} /> : <Play className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" strokeWidth={2.4} />}
          </button>

          <div
            role="slider"
            tabIndex={0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="flex-1 h-1.5 sm:h-2 rounded-full bg-brand-cream/20 cursor-pointer relative touch-none select-none"
            onPointerDown={onSeekPointerDown}
            onPointerMove={onSeekPointerMove}
            onPointerUp={onSeekPointerUp}
            onPointerCancel={onSeekPointerUp}
            data-testid="ad-video-progress"
          >
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-brand-turmeric"
              style={{ width: `${progress * 100}%` }}
            />
          </div>

          <span className="text-xs sm:text-sm tabular-nums text-brand-cream/90 shrink-0" data-testid="ad-video-time">
            {fmt(currentTime)} / {fmt(duration)}
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEnlarged((prev) => !prev);
              showControls();
            }}
            className={`${enlarged ? "w-11 h-11 sm:w-12 sm:h-12" : "w-9 h-9 sm:w-10 sm:h-10"} rounded-full bg-brand-cream/15 hover:bg-brand-cream/25 text-brand-cream flex items-center justify-center shrink-0 transition-all active:scale-95`}
            aria-label={enlarged ? "Exit enlarged view" : "Enlarge video"}
            data-testid={enlarged ? "ad-video-shrink" : "ad-video-enlarge"}
          >
            {enlarged ? (
              <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
            ) : (
              <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
            )}
          </button>
        </div>
      </div>
      {videos.length > 1 && !enlarged && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-3 sm:bottom-4 flex items-center gap-2 z-30"
          data-testid="ad-video-dots"
        >
          {videos.map((v, idx) => (
            <button
              key={v.id || idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (loopTimerRef.current) {
                  clearTimeout(loopTimerRef.current);
                  loopTimerRef.current = null;
                }
                setBlackScreen(false);
                setVideoIdx(idx);
              }}
              aria-label={`Play video ${idx + 1}`}
              data-testid={`ad-video-dot-${idx}`}
              className={`h-2 rounded-full transition-all ${
                idx === videoIdx
                  ? "w-6 bg-brand-cream"
                  : "w-2 bg-brand-cream/50 hover:bg-brand-cream/80"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section
      className="bg-brand-dark py-4 sm:py-6 lg:py-8"
      data-testid="ad-video-section"
    >
      {/* iter51: tighter outer padding + slightly larger video (max-w-7xl)
          with consistent rounded corners on every breakpoint. Section height
          stays the same — we just reclaim the previously-wasted top/bottom
          space and let the player fill it. */}
      <div className="px-3 sm:px-6 lg:px-8">{sectionContent}</div>
    </section>
  );
}
